import { environment } from '../../../environments/environment';

/** Strips trailing slashes from a URL. */
export function stripTrailingSlash(url: string): string {
  return url.trim().replace(/\/+$/, '');
}

/** Canonical app origin (env), falling back to the browser locally. */
export function getAppOrigin(): string {
  const configured = environment.appBaseUrl?.trim();
  if (configured) return stripTrailingSlash(configured);
  if (typeof window !== 'undefined') return stripTrailingSlash(window.location.origin);
  return '';
}

/**
 * Live browser origin for OAuth redirects.
 * Must match where `oauth_nonce` was stored — env `localhost` ≠ `127.0.0.1`.
 */
export function getBrowserOrigin(): string {
  if (typeof window !== 'undefined') return stripTrailingSlash(window.location.origin);
  return getAppOrigin();
}

/**
 * Builds the public client validation URL.
 * Devis uses `/validation/...`; files and BAT share `/bat/...`.
 * @param token - Public token for the target step
 * @param segment - Validation step segment (`quote`, `files`, or `proof`)
 * @returns Absolute public URL for the given step
 */
export function buildPublicValidationUrl(token: string, segment: 'quote' | 'proof' | 'files'): string {
  const origin = getAppOrigin();
  if (segment === 'quote') return `${origin}/validation/${token}/quote`;
  return `${origin}/bat/${token}/${segment}`;
}

/** Builds a Dropbox folder URL from its path. */
export function buildDropboxFolderUrl(folderPath: string): string {
  const encoded = folderPath.split('/').map(encodeURIComponent).join('/');
  return `${environment.dropboxHomeBaseUrl}${encoded}`;
}

/** Builds a Sellsy opportunity URL. */
export function buildSellsyOpportunityUrl(sellsyId: number | string): string {
  return `${environment.sellsyOpportunityBaseUrl}/${sellsyId}`;
}

/** Builds a Sellsy estimate (devis) URL (`?_f=estimateOverview&id=<id>`: the document overview screen). */
export function buildSellsyEstimateUrl(sellsyEstimateId: number | string): string {
  return `${environment.sellsyEstimateBaseUrl}${sellsyEstimateId}`;
}

/** Builds the Microsoft OAuth authorization URL for an Azure tenant. */
export function buildMicrosoftAuthorizeUrl(tenantId: string): string {
  return `${environment.microsoftLoginBaseUrl}/${tenantId}/oauth2/v2.0/authorize`;
}

/** Builds the address search URL (French API). */
export function buildAddressSearchUrl(query: string): string {
  return `${environment.addressApiBaseUrl}/search/?q=${encodeURIComponent(query)}&limit=6&autocomplete=1`;
}

/** Returns the Adobe PDF Embed SDK URL. */
export function getAdobeSdkUrl(): string {
  return environment.adobeSdkUrl;
}
